
import { supabase } from './client';

export const getUserProfile = async (userId: string) => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();
      
    if (error) throw error;
    
    return { data, error: null };
  } catch (error) {
    console.error('Error getting user profile:', error);
    return { data: null, error };
  }
};

// Get the family profile for a user from Supabase
export const getFamilyProfileFromSupabase = async (userId: string) => {
  try {
    // Get the main family profile - use limit(1) instead of single()
    const { data: familyProfiles, error: familyError } = await supabase
      .from('family_profiles')
      .select('*')
      .eq('user_id', userId)
      .limit(1);
      
    if (familyError) throw familyError;
    
    // No profiles found
    if (!familyProfiles || familyProfiles.length === 0) {
      return { data: null, error: null };
    }
    
    // Use the first profile
    const familyProfile = familyProfiles[0];
    
    // Get siblings for this family profile
    const { data: siblings, error: siblingsError } = await supabase
      .from('siblings')
      .select('*')
      .eq('family_profile_id', familyProfile.id);
      
    if (siblingsError) throw siblingsError;
    
    // Get grandparents for this family profile
    const { data: grandparents, error: grandparentsError } = await supabase
      .from('grandparents')
      .select('*')
      .eq('family_profile_id', familyProfile.id);
      
    if (grandparentsError) throw grandparentsError;
    
    // Deduplicate grandparents by ID
    const uniqueGrandparents = grandparents ? 
      Array.from(new Map(grandparents.map(g => [g.id, g])).values()) : 
      [];
    
    // Format family profile data to match the application structure
    const formattedProfile = {
      id: familyProfile.id,
      motherName: familyProfile.mother_name,
      fatherName: familyProfile.father_name,
      preferMetricHeight: familyProfile.prefer_metric_height,
      preferMetricWeight: familyProfile.prefer_metric_weight,
      siblings: siblings?.map(sibling => ({
        id: sibling.id,
        name: sibling.name,
        birthday: sibling.birthday
      })) || [],
      grandparents: uniqueGrandparents.map(grandparent => ({
        id: grandparent.id,
        name: grandparent.name,
        relation: grandparent.relation as 'maternal' | 'paternal'
      })) || []
    };
    
    return { data: formattedProfile, error: null };
  } catch (error) {
    console.error('Error getting family profile from Supabase:', error);
    return { data: null, error };
  }
};

// Simple ping function to test connectivity
export const pingSupabase = async () => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('count(*)', { count: 'exact', head: true });
      
    if (error) throw error;
    
    return { success: true, error: null };
  } catch (error) {
    console.error('Error pinging Supabase:', error);
    return { success: false, error };
  }
};
